import React from 'react';
import { motion } from 'framer-motion';
import {
    Wind,
    Search,
    Menu,
    User,
    ChevronLeft,
    ChevronRight,
    RefreshCw,
    AlertTriangle
} from 'lucide-react';

const WindPowerMonitoring: React.FC = () => {

    const turbineData = {
        totalEnergy: "4.2 GWh",
        power10m: "157.2 k/W",
        windSpeed: "5.9 m/s",
        direction: "164 kW",
        twist: "-1.05",
        velocity: "0.42 m/s",
        power: "125 kW",
        bearingTemp: "69 °C",
        ambientTemp: "7.7 °C",
        rotorTemp: "29 °C"
    };

    const windEnergyData = [
        { time: "00:00", power: 120, windSpeed: 4.2 },
        { time: "04:00", power: 140, windSpeed: 5.1 },
        { time: "08:00", power: 160, windSpeed: 6.3 },
        { time: "12:00", power: 180, windSpeed: 7.2 },
        { time: "16:00", power: 150, windSpeed: 5.8 },
        { time: "20:00", power: 130, windSpeed: 4.9 }
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
            {/* Header */}
            <motion.header
                className="bg-white shadow-sm border-b border-gray-200 px-6 py-4"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                            <Wind className="w-8 h-8 text-blue-600" />
                            <span className="text-xl font-bold text-gray-900">WindFarm</span>
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        <Search className="w-5 h-5 text-gray-400" />
                        <Menu className="w-5 h-5 text-gray-400" />
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600">40% Energy Received</span>
                            <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></div>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <User className="w-6 h-6 text-gray-600" />
                        <span className="text-sm font-medium text-gray-900">Adam Driver</span>
                    </div>
                </div>
            </motion.header>

            <div className="flex h-screen">
                {/* Left Side - Wind Turbine Visual */}
                <motion.div
                    className="flex-1 relative bg-gradient-to-br from-blue-100 to-blue-50 p-8"
                    initial={{ opacity: 0, x: -50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8 }}
                >
                    {/* 3D Wind Turbine Visual */}
                    <div className="relative h-full flex items-center justify-center">
                        <div className="relative">
                            {/* Wind Turbine Base */}
                            <div className="w-32 h-64 bg-gray-300 rounded-full mx-auto relative">
                                {/* Turbine Blades */}
                                <div className="absolute top-0 left-1/2 transform -translate-x-1/2">
                                    <div className="w-1 h-24 bg-gray-600 rounded-full"></div>
                                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-16 h-1 bg-gray-600 rounded-full"></div>
                                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-16 h-1 bg-gray-600 rounded-full rotate-120"></div>
                                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-16 h-1 bg-gray-600 rounded-full -rotate-120"></div>
                                </div>

                                {/* Turbine Hub */}
                                <div className="absolute top-8 left-1/2 transform -translate-x-1/2 w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center">
                                    <span className="text-xs font-bold text-white">1</span>
                                </div>
                            </div>

                            {/* Zoom Controls */}
                            <div className="absolute left-4 top-1/2 transform -translate-y-1/2 flex flex-col gap-2">
                                <button className="w-8 h-8 bg-white rounded shadow-md flex items-center justify-center hover:bg-gray-50">
                                    <span className="text-lg font-bold">+</span>
                                </button>
                                <button className="w-8 h-8 bg-white rounded shadow-md flex items-center justify-center hover:bg-gray-50">
                                    <span className="text-lg font-bold">-</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Status Popup */}
                    <motion.div
                        className="absolute top-1/4 right-8 bg-gray-800 text-white p-4 rounded-lg shadow-lg"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.5, duration: 0.6 }}
                    >
                        <div className="space-y-3">
                            <div className="flex items-center gap-2">
                                <span className="text-sm">Status</span>
                                <div className="flex items-center gap-1">
                                    <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                                    <span className="text-sm font-medium">Active</span>
                                </div>
                            </div>

                            <div className="space-y-2 text-sm">
                                <div>Rotor: Pitch Control</div>
                                <div>Nacelle: On Wind</div>
                                <div>Converter: Running</div>
                            </div>

                            <div className="flex gap-2">
                                <button className="w-6 h-6 bg-gray-700 rounded flex items-center justify-center">
                                    <ChevronLeft className="w-3 h-3" />
                                </button>
                                <button className="w-6 h-6 bg-gray-700 rounded flex items-center justify-center">
                                    <ChevronRight className="w-3 h-3" />
                                </button>
                            </div>
                        </div>
                    </motion.div>

                    {/* Bottom Title */}
                    <div className="absolute bottom-8 left-8">
                        <h1 className="text-3xl font-bold text-gray-900 mb-2">Wind Power Generation</h1>
                        <div className="text-sm text-gray-600">
                            <div>Wed, 27th</div>
                            <div>Now Online</div>
                        </div>
                    </div>
                </motion.div>

                {/* Right Side - Data Dashboard */}
                <motion.div
                    className="w-1/2 bg-gray-50 p-6 overflow-y-auto"
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                >
                    <div className="grid grid-cols-2 gap-6">
                        {/* Wind Energy Chart */}
                        <motion.div
                            className="bg-white rounded-lg p-6 shadow-sm border border-gray-200"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4 }}
                        >
                            <h3 className="text-lg font-bold text-gray-900 mb-4">Wind Energy</h3>
                            <div className="h-32 relative">
                                {/* Simple Bar Chart */}
                                <div className="flex items-end justify-between h-full">
                                    {windEnergyData.map((data, index) => (
                                        <div key={index} className="flex flex-col items-center">
                                            <div
                                                className="w-6 bg-red-500 rounded-t"
                                                style={{ height: `${(data.power / 200) * 100}%` }}
                                            ></div>
                                            <div className="text-xs text-gray-500 mt-2">{data.time}</div>
                                        </div>
                                    ))}
                                </div>
                                {/* Wind Speed Line */}
                                <div className="absolute top-0 left-0 w-full h-full">
                                    <svg className="w-full h-full">
                                        <polyline
                                            points={windEnergyData.map((_, i) => `${(i / (windEnergyData.length - 1)) * 100},${100 - (windEnergyData[i].windSpeed / 10) * 100}`).join(' ')}
                                            fill="none"
                                            stroke="#6B7280"
                                            strokeWidth="2"
                                        />
                                    </svg>
                                </div>
                            </div>
                            <div className="flex gap-4 mt-4 text-xs">
                                <div className="flex items-center gap-1">
                                    <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                                    <span>wind speed</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                                    <span>power</span>
                                </div>
                            </div>
                        </motion.div>

                        {/* Wind & Nacelle */}
                        <motion.div
                            className="bg-white rounded-lg p-6 shadow-sm border border-gray-200"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.5 }}
                        >
                            <h3 className="text-lg font-bold text-gray-900 mb-4">Wind & Nacelle</h3>
                            <div className="text-center">
                                {/* Compass */}
                                <div className="relative w-24 h-24 mx-auto mb-4">
                                    <div className="w-full h-full border-2 border-gray-300 rounded-full relative">
                                        <div className="absolute inset-2 border border-gray-200 rounded-full flex items-center justify-center">
                                            <div className="text-center">
                                                <div className="text-lg font-bold text-gray-900">Direction</div>
                                                <div className="text-2xl font-bold text-gray-900">{turbineData.direction}</div>
                                            </div>
                                        </div>
                                        {/* Compass Points */}
                                        <div className="absolute top-1 left-1/2 transform -translate-x-1/2 text-xs font-bold">N</div>
                                        <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 text-xs font-bold">S</div>
                                        <div className="absolute left-1 top-1/2 transform -translate-y-1/2 text-xs font-bold">W</div>
                                        <div className="absolute right-1 top-1/2 transform -translate-y-1/2 text-xs font-bold">E</div>
                                    </div>
                                </div>

                                <div className="space-y-2 text-sm text-gray-600">
                                    <div>Twist {turbineData.twist}</div>
                                    <div>Avg. Speed {turbineData.windSpeed}</div>
                                    <div>Velocity {turbineData.velocity}</div>
                                </div>
                            </div>
                        </motion.div>

                        {/* Total Energy */}
                        <motion.div
                            className="bg-white rounded-lg p-6 shadow-sm border border-gray-200"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.6 }}
                        >
                            <h3 className="text-lg font-bold text-gray-900 mb-4">Total Energy</h3>
                            <div className="text-3xl font-bold text-gray-900">{turbineData.totalEnergy}</div>
                        </motion.div>

                        {/* Power (10m) */}
                        <motion.div
                            className="bg-white rounded-lg p-6 shadow-sm border border-gray-200"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.7 }}
                        >
                            <h3 className="text-lg font-bold text-gray-900 mb-4">Power (10m)</h3>
                            <div className="text-3xl font-bold text-gray-900">{turbineData.power10m}</div>
                        </motion.div>

                        {/* Overview */}
                        <motion.div
                            className="bg-white rounded-lg p-6 shadow-sm border border-gray-200"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.8 }}
                        >
                            <h3 className="text-lg font-bold text-gray-900 mb-4">Overview</h3>
                            <div className="text-center">
                                {/* Radial Gauge */}
                                <div className="relative w-20 h-20 mx-auto mb-4">
                                    <div className="w-full h-full border-4 border-gray-200 rounded-full relative">
                                        <div className="absolute inset-2 border-4 border-red-500 rounded-full" style={{ clipPath: 'polygon(50% 50%, 50% 0%, 75% 25%, 50% 50%)' }}></div>
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <div className="text-center">
                                                <div className="text-sm font-bold text-gray-900">Power</div>
                                                <div className="text-lg font-bold text-gray-900">{turbineData.power}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <button className="bg-red-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-600 transition-colors flex items-center gap-2 mx-auto">
                                    <RefreshCw className="w-4 h-4" />
                                    Reload
                                </button>
                            </div>
                        </motion.div>

                        {/* Temperature */}
                        <motion.div
                            className="bg-white rounded-lg p-6 shadow-sm border border-gray-200"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.9 }}
                        >
                            <h3 className="text-lg font-bold text-gray-900 mb-4">Temperature</h3>
                            <div className="space-y-4">
                                {/* Bearing */}
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm text-gray-600">Bearing</span>
                                        <div className="flex items-center gap-1">
                                            <span className="text-sm font-medium">{turbineData.bearingTemp}</span>
                                            <AlertTriangle className="w-4 h-4 text-red-500" />
                                        </div>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                        <div className="bg-red-500 h-2 rounded-full" style={{ width: '69%' }}></div>
                                    </div>
                                </div>

                                {/* Ambient */}
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm text-gray-600">Ambient</span>
                                        <span className="text-sm font-medium">{turbineData.ambientTemp}</span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                        <div className="bg-blue-500 h-2 rounded-full" style={{ width: '30%' }}></div>
                                    </div>
                                </div>

                                {/* Rotor */}
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm text-gray-600">Rotor</span>
                                        <span className="text-sm font-medium">{turbineData.rotorTemp}</span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                        <div className="bg-green-500 h-2 rounded-full" style={{ width: '45%' }}></div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default WindPowerMonitoring;
